// Mirrors mission-x-api api/equipment/equipment.const.js - the plan item units minus
// "Recurring", which the financials engine prices at 0.
export const EQUIPMENT_RATE_UNITS = ["Hourly", "Daily", "Weekly", "Monthly", "Yearly", "One time"]

// Machines are usually hired by the day, not the hour.
export const EQUIPMENT_DEFAULT_RATE_UNIT = "Daily"

// Mirrors equipment.rules.js#canManageEquipment - create/update/archive access.
export const canManageEquipment = (meInOrg) => {
    return meInOrg?.permission === 2 || Boolean(meInOrg?.canManageEquipment)
}

// Mirrors equipment.rules.js#canScheduleEquipment (= canReadEquipment) - booking units
// onto days and missions: admins, managers, limited managers and equipment managers.
export const canScheduleEquipment = (meInOrg) => {
    return (
        meInOrg?.permission === 2 ||
        meInOrg?.permission === 4 ||
        (meInOrg?.permission === 1 && !meInOrg?.isBasicCompanyAdmin) ||
        Boolean(meInOrg?.canManageEquipment)
    )
}

const isSet = (v) => v !== null && v !== undefined && v !== ""

// "One time" reads "Per use" on equipment only - the stored value stays the People one.
export const formatEquipmentRateUnit = (unit) => (unit === "One time" ? "Per use" : unit)

// Field enums - mirror the API's api/equipment/equipment.fields.js.
export const EQUIPMENT_STATUSES = [
    "Available",
    "In Use",
    "Reserved",
    "Under Maintenance",
    "Out of Service",
    "Retired",
    "Lost",
]
export const EQUIPMENT_UNBOOKABLE_STATUSES = ["Out of Service", "Retired", "Lost"]
export const EQUIPMENT_CONDITIONS = ["New", "Good", "Fair", "Poor"]
export const EQUIPMENT_OWNERSHIP_TYPES = ["Owned", "Leased", "Rented", "Client-Supplied"]
export const EQUIPMENT_LEASED_OWNERSHIP = ["Leased", "Rented"]
export const EQUIPMENT_DEPRECIATION_METHODS = ["None", "Straight-Line", "Declining Balance"]
export const EQUIPMENT_SERVICE_INTERVALS = ["None", "Days", "Usage"]
export const EQUIPMENT_METER_UNITS = ["Hours", "Kilometers", "Miles", "Cycles"]
export const EQUIPMENT_EXPORT_CONTROLS = ["None", "ITAR", "EAR"]
export const EQUIPMENT_MAX_PHOTOS = 10
export const EQUIPMENT_DUE_SOON_DAYS = 30

const DAY_MS = 86400000

export const getEquipmentStatus = (item) => item?.status || "Available"

/**
 * Why `meInOrg` can't book `item` right now, or null - the scheduler's gate, mirroring
 * the API's equipment.utils.js#canBookEquipment.
 *
 * - hidden: archived, not schedulable, Out of Service / Retired / Lost, or restricted
 *   to others - never offered in scheduling pickers.
 * - shown with a reason: an open High / Out of service maintenance request, or (with
 *   the blocking workflow on) calibration / service overdue.
 *
 * @param item {Object} an equipment unit
 * @param [meInOrg] {Object} the org person booking ({ ref, role }); leave out to only
 *   ask whether the unit can be booked by anyone
 * @param [options] {{ blockOverdue: Boolean, now: Number }}
 * @return {{ code, reason, hidden }|null}
 */
export const getEquipmentBlock = (item, meInOrg, { blockOverdue = false, now = Date.now() } = {}) => {
    if (!item || item.isArchived) return { code: "archived", reason: "Archived", hidden: true }
    if (item.isSchedulable === false) return { code: "not_schedulable", reason: "Not schedulable", hidden: true }
    if (EQUIPMENT_UNBOOKABLE_STATUSES.includes(getEquipmentStatus(item))) {
        return { code: "status", reason: getEquipmentStatus(item), hidden: true }
    }

    if (item.isRestricted && meInOrg) {
        const personId = String(meInOrg.ref?._id || meInOrg.ref || "")
        const expired = new Set(
            (item.accessGrants || []).filter((g) => g.expiresAt && g.expiresAt <= now).map((g) => String(g.person)),
        )
        const people = (item.restrictedTo?.people || []).map(String).filter((id) => !expired.has(id))
        const roles = (item.restrictedTo?.roles || []).map(String)
        const allowed = people.includes(personId) || (Boolean(meInOrg.role) && roles.includes(String(meInOrg.role)))

        if (!allowed) return { code: "restricted", reason: "Restricted", hidden: true }
    }

    if (item.maintenanceBlock?.since) {
        return {
            code: "maintenance",
            reason: item.maintenanceBlock.reason || "Under maintenance",
        }
    }

    if (blockOverdue) {
        const isPast = (date) => Boolean(date) && date + DAY_MS <= now

        if (item.requiresCalibration && isPast(item.nextCalibrationDue)) {
            return { code: "calibration_due", reason: "Calibration overdue - update the date to book it again" }
        }

        if (isPast(getNextServiceDue(item))) {
            return { code: "service_due", reason: "Service overdue - update the date to book it again" }
        }
    }

    return null
}

/**
 * Whether `meInOrg` may book `item` - see getEquipmentBlock.
 */
export const isEquipmentBookable = (item, meInOrg, options) => !getEquipmentBlock(item, meInOrg, options)

const elapsedMonths = (from, to) => {
    if (!from || !to || to <= from) return 0
    const a = new Date(from)
    const b = new Date(to)
    let months = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth())
    if (b.getUTCDate() < a.getUTCDate()) months -= 1
    return Math.max(0, months)
}

const round2 = (v) => Math.round(v * 100) / 100

/**
 * Current book value - mirrors the API's equipment.depreciation.js#getEquipmentBookValue.
 * Straight-line, or double-declining (monthly rate 2 / useful life) never below salvage.
 * @return {Number|null}
 */
export const getEquipmentBookValue = (item = {}, now = Date.now()) => {
    const price = item.purchasePrice
    if (!isSet(price)) return null

    const method = item.depreciationMethod || "None"
    if (method === "None") return round2(price)

    const life = item.usefulLifeMonths
    if (!life || life <= 0) return null

    const salvage = Math.min(item.salvageValue || 0, price)
    const months = Math.min(elapsedMonths(item.purchaseDate, now), life)

    if (months >= life) return round2(salvage)
    if (method === "Straight-Line") return round2(price - ((price - salvage) * months) / life)

    return round2(Math.max(salvage, price * Math.pow(1 - Math.min(1, 2 / life), months)))
}

/**
 * The stored override, else last service + interval for Days intervals.
 */
export const getNextServiceDue = (item = {}) => {
    if (item.nextServiceDue) return item.nextServiceDue
    if (item.serviceIntervalType === "Days" && item.lastServiceDate && item.serviceIntervalValue) {
        return item.lastServiceDate + item.serviceIntervalValue * DAY_MS
    }
    return null
}

export const EQUIPMENT_DUE_DATE_LABELS = {
    nextServiceDue: "Service",
    nextCalibrationDue: "Calibration",
    warrantyExpiry: "Warranty",
    insuranceExpiry: "Insurance",
    inspectionExpiry: "Inspection",
}

/**
 * Dates within 30 days (overdue included) - the "due soon" badge. Mirrors the API's
 * equipment.utils.js#getEquipmentDueSoon.
 * @return {Array<{ field, label, date, overdue }>}
 */
export const getEquipmentDueSoon = (item = {}, now = Date.now()) => {
    const until = now + EQUIPMENT_DUE_SOON_DAYS * DAY_MS

    return Object.keys(EQUIPMENT_DUE_DATE_LABELS)
        .map((field) => ({
            field,
            label: EQUIPMENT_DUE_DATE_LABELS[field],
            date: field === "nextServiceDue" ? getNextServiceDue(item) : item[field],
        }))
        .filter(({ field, date }) => date && date <= until && (field !== "nextCalibrationDue" || item.requiresCalibration))
        .map((due) => ({ ...due, overdue: due.date < now }))
}

const shortDate = (ms) => new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })

const UNAVAILABLE_STATUSES = ["Out of Service", "Retired", "Lost", "Under Maintenance"]

/**
 * What's wrong with a unit, for the list's warning triangle. `stats` is its entry from
 * GET /equipment/items/stats (bookings today and the next 90 days). Red needs doing
 * now; orange is coming up.
 *
 * @return {{ level: "red"|"orange"|null, warnings: Array<{ level, text }> }}
 */
export const getEquipmentWarnings = (item = {}, stats = {}, now = Date.now()) => {
    const warnings = []
    const red = (text) => warnings.push({ level: "red", text })
    const orange = (text) => warnings.push({ level: "orange", text })
    const status = getEquipmentStatus(item)
    const upcoming = stats.upcoming || []
    const isBooked = stats.bookedNow || upcoming.length > 0

    // Broken / gone
    if (status === "Out of Service") red("Out of service")
    if (status === "Lost") red("Lost")
    if (status === "Under Maintenance") orange("Under maintenance")
    if (item.condition === "Poor") orange("In poor condition")

    // Expiring / due
    getEquipmentDueSoon(item, now).forEach((due) => {
        const what = due.field === "nextCalibrationDue" ? "Calibration certificate" : due.label
        if (due.overdue) {
            red(`${what} ${due.field === "nextServiceDue" ? "overdue" : "expired"} since ${shortDate(due.date)}`)
        } else {
            orange(`${what} ${due.field === "nextServiceDue" ? "due" : "expires"} ${shortDate(due.date)}`)
        }
    })

    if (EQUIPMENT_LEASED_OWNERSHIP.includes(item.ownershipType) && item.leaseEndDate) {
        if (item.leaseEndDate < now) red(`Lease ended ${shortDate(item.leaseEndDate)}`)
        else if (item.leaseEndDate <= now + EQUIPMENT_DUE_SOON_DAYS * DAY_MS)
            orange(`Lease ends ${shortDate(item.leaseEndDate)}`)
    }

    if (item.requiresCalibration && !item.nextCalibrationDue) orange("Needs calibrating, but has no calibration due date")
    if (item.isHazardous && !item.sdsDocument) orange("Hazardous, but no safety data sheet")

    // Overbooked
    if (isBooked && UNAVAILABLE_STATUSES.includes(status)) {
        red(`Booked while ${status.toLowerCase()}`)
    }

    const serviceDue = getNextServiceDue(item)
    if (serviceDue && upcoming.some((b) => b.startDate <= serviceDue && b.endDate > serviceDue)) {
        orange(`Booked across its service date (${shortDate(serviceDue)})`)
    }

    if (item.setupBufferHours > 0) {
        const tight = upcoming.some((b, i) => {
            const next = upcoming[i + 1]
            return next && next.startDate - b.endDate < item.setupBufferHours * 3600000
        })
        if (tight) orange(`Back-to-back bookings leave no room for its ${item.setupBufferHours}h setup buffer`)
    }

    return {
        level: warnings.some((w) => w.level === "red") ? "red" : warnings.length ? "orange" : null,
        warnings: [...warnings.filter((w) => w.level === "red"), ...warnings.filter((w) => w.level === "orange")],
    }
}
